import type { MaskitoOptions } from '@maskito/core';
import { maskitoNumberOptionsGenerator, type MaskitoNumberParams } from '@maskito/kit';

/**
 * Options for {@link BrightsideCurrencyMaskDirective} (`input[currencyMask]`).
 * Extends Maskito number params; `align` sets `text-align` on the host input.
 * `nullable` mirrors ngx-currency: empty input maps to `null` in the form model.
 * Legacy ngx-only fields (`inputMode`, etc.) are ignored.
 */
export type BrightsideCurrencyMaskOptions = Partial<MaskitoNumberParams> & {
  align?: string;
  nullable?: boolean;
  /** When false, `min` defaults to `0` (ngx-currency parity). */
  allowNegative?: boolean;
};

const LEGACY_KEYS = new Set(['inputMode', 'allowZero', 'decimal', 'thousands', 'suffix']);

function sanitizeOptions(raw: BrightsideCurrencyMaskOptions | null | undefined): BrightsideCurrencyMaskOptions {
  if (!raw) {
    return {};
  }
  const out: BrightsideCurrencyMaskOptions = { ...raw };
  for (const k of LEGACY_KEYS) {
    delete (out as Record<string, unknown>)[k];
  }
  return out;
}

/**
 * Builds {@link MaskitoNumberParams} for {@link maskitoParseNumber} / {@link maskitoStringifyNumber},
 * using the same defaults previously applied via ngx-currency in Brightside UI.
 */
export function brightsideCurrencyNumberParams(overrides: BrightsideCurrencyMaskOptions = {}): MaskitoNumberParams {
  const o = sanitizeOptions(overrides);
  const allowNegative = o.allowNegative ?? false;
  const precision = o.precision ?? o.maximumFractionDigits ?? 2;
  return {
    prefix: '$',
    thousandSeparator: ',',
    decimalSeparator: '.',
    precision,
    maximumFractionDigits: o.maximumFractionDigits ?? precision,
    minimumFractionDigits: o.minimumFractionDigits ?? 0,
    min: allowNegative ? (o.min ?? Number.MIN_SAFE_INTEGER) : (o.min ?? 0),
    max: o.max ?? Number.MAX_SAFE_INTEGER,
    minusSign: o.minusSign,
    minusPseudoSigns: o.minusPseudoSigns,
    negativePattern: o.negativePattern,
    postfix: o.postfix,
    decimalPseudoSeparators: o.decimalPseudoSeparators,
  };
}

/** Full Maskito options for a Brightside USD-style currency field. */
export function brightsideCurrencyMaskitoOptions(overrides: BrightsideCurrencyMaskOptions = {}): MaskitoOptions {
  return maskitoNumberOptionsGenerator(brightsideCurrencyNumberParams(overrides));
}
